import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

export interface BannerAction {
    label: string;
    route?: string;
    variant?: 'primary' | 'text';
}

export interface BannerOptions {
    title: string;
    message: string;
    icon?: string;
    variant?: 'warning' | 'info' | 'error';
    dismissLabel?: string;
    actions?: BannerAction[];
    /** Unique key used to persist dismissal in localStorage for 30 days. Omit for session-only dismiss. */
    dismissKey?: string;
}

const DISMISS_PREFIX = 'banner_dismissed_';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class BannerService {
    private readonly _banner$ = new BehaviorSubject<BannerOptions | null>(null);
    banner$ = this._banner$.asObservable();

    constructor(private readonly router: Router) {}

    showBanner(options: BannerOptions): void {
        if (options.dismissKey && this.isDismissedWithin30Days(options.dismissKey)) {
            return;
        }
        this._banner$.next(options);
    }

    dismissBanner(): void {
        const current = this._banner$.getValue();
        if (current?.dismissKey) {
            localStorage.setItem(`${DISMISS_PREFIX}${current.dismissKey}`, Date.now().toString());
        }
        this._banner$.next(null);
    }

    clearBanner(): void {
        this._banner$.next(null);
    }

    navigate(url: string): void {
        this.router.navigate([url]);
    }

    clearDismissal(key: string): void {
        localStorage.removeItem(`${DISMISS_PREFIX}${key}`);
    }

    private isDismissedWithin30Days(key: string): boolean {
        const stored = localStorage.getItem(`${DISMISS_PREFIX}${key}`);
        if (!stored) return false;
        return Date.now() - Number.parseInt(stored, 10) < THIRTY_DAYS_MS;
    }
}
